import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { metaEmbeddedSignupApi } from '../../../services/api'
import { useNotification } from '../../../contexts/NotificationContext'
import {
  META_EMBEDDED_SIGNUP_CANCELLED_NOTIFICATION,
  META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION,
  META_EMBEDDED_SIGNUP_LOGIN_EXTRAS,
  META_EMBEDDED_SIGNUP_TIMEOUT_MS,
  META_EMBEDDED_SIGNUP_TIMEOUT_NOTIFICATION,
  META_EMBEDDED_SIGNUP_UNAVAILABLE_NOTIFICATION,
} from '../constants'
import type {
  FlowStatus,
  SignupSession,
  UseMetaEmbeddedSignupOptions,
  UseMetaEmbeddedSignupResult,
} from '../types'
import {
  buildCompletionPayload,
  extractApiErrorMessage,
  getButtonLabel,
  getMetaEnv,
  hasMetaError,
  isEmbeddedSignupMessage,
  isFacebookOrigin,
  isSuccessEvent,
  parseMetaMessageData,
} from '../utils'
import { useFacebookSdk } from './useFacebookSdk'

export function useMetaEmbeddedSignup({
  disabled = false,
  smbId,
  onCompleted,
}: UseMetaEmbeddedSignupOptions): UseMetaEmbeddedSignupResult {
  const { showNotification } = useNotification()
  const { appId, configurationId, graphApiVersion } = useMemo(getMetaEnv, [])
  const [flowStatus, setFlowStatus] = useState<FlowStatus>('idle')
  const [loginCode, setLoginCode] = useState<string | null>(null)
  const [signupSession, setSignupSession] = useState<SignupSession | null>(null)
  const activeFlowRef = useRef(false)
  const submittingRef = useRef(false)
  const submittedKeyRef = useRef<string | null>(null)
  const onCompletedRef = useRef(onCompleted)

  const configError = useMemo(() => {
    if (!appId) {
      return 'Meta App ID is not configured. Set VITE_META_APP_ID before connecting WhatsApp.'
    }
    if (!configurationId) {
      return 'Meta Embedded Signup configuration ID is not configured. Set VITE_META_EMBEDDED_SIGNUP_CONFIG_ID before connecting WhatsApp.'
    }
    return null
  }, [appId, configurationId])

  const setVisibleError = useCallback((errorMessage: string) => {
    setFlowStatus('error')
    showNotification(errorMessage, 'error', 10000)
  }, [showNotification])

  const { prepareSdk, sdkStatus } = useFacebookSdk({
    appId,
    configError,
    graphApiVersion,
    onError: setVisibleError,
  })

  useEffect(() => {
    onCompletedRef.current = onCompleted
  }, [onCompleted])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isFacebookOrigin(event.origin)) {
        console.debug('[MetaEmbeddedSignup] Ignored message from non-Facebook origin:', event.origin)
        return
      }

      let data: unknown
      try {
        data = parseMetaMessageData(event.data)
      } catch {
        console.debug('[MetaEmbeddedSignup] Ignored non-JSON Facebook message:', event.data)
        return
      }

      if (!isEmbeddedSignupMessage(data)) {
        return
      }

      console.debug('[MetaEmbeddedSignup] Message event:', data)

      if (!activeFlowRef.current) {
        return
      }

      const eventName = data.event || ''

      if (eventName === 'CANCEL') {
        const isErrorCancel = hasMetaError(data.data)
        activeFlowRef.current = false
        submittedKeyRef.current = null
        setLoginCode(null)
        setSignupSession(null)
        setFlowStatus(isErrorCancel ? 'error' : 'cancelled')
        showNotification(
          isErrorCancel
            ? META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION
            : META_EMBEDDED_SIGNUP_CANCELLED_NOTIFICATION,
          isErrorCancel ? 'error' : 'warning',
          10000
        )
        return
      }

      if (eventName === 'ERROR' || hasMetaError(data.data)) {
        activeFlowRef.current = false
        submittedKeyRef.current = null
        setLoginCode(null)
        setSignupSession(null)
        setVisibleError(META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION)
        return
      }

      if (!eventName || !isSuccessEvent(eventName)) {
        activeFlowRef.current = false
        setVisibleError(META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION)
        return
      }

      setSignupSession({
        event: eventName,
        data: data.data || {},
      })
      setFlowStatus((current) => (current === 'submitting' ? current : 'waiting'))
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [setVisibleError, showNotification])

  useEffect(() => {
    if (!loginCode || !signupSession || submittingRef.current) {
      return
    }

    const submitKey = [
      loginCode,
      signupSession.event,
      signupSession.data.waba_id || '',
      signupSession.data.phone_number_id || '',
      signupSession.data.business_id || '',
    ].join(':')

    if (submittedKeyRef.current === submitKey) {
      return
    }

    submittedKeyRef.current = submitKey
    submittingRef.current = true
    setFlowStatus('submitting')

    const submitSignup = async () => {
      try {
        const response = await metaEmbeddedSignupApi.completeSignup(
          buildCompletionPayload(loginCode, signupSession, smbId)
        )
        const smbName = response.smb.display_name || response.smb.name || 'business'
        activeFlowRef.current = false
        setFlowStatus('success')
        showNotification(`WhatsApp Business connected for ${smbName}!`, 'success')
        await onCompletedRef.current?.(response)
      } catch (error) {
        console.error('[MetaEmbeddedSignup] Completion failed:', error)
        submittedKeyRef.current = null
        activeFlowRef.current = false
        const apiErrorMessage = extractApiErrorMessage(error)
        setVisibleError(apiErrorMessage ?? META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION)
      } finally {
        submittingRef.current = false
      }
    }

    void submitSignup()
  }, [loginCode, setVisibleError, showNotification, signupSession])

  useEffect(() => {
    if (flowStatus !== 'waiting' || !activeFlowRef.current || (loginCode && signupSession)) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      if (!activeFlowRef.current || (loginCode && signupSession)) {
        return
      }

      activeFlowRef.current = false
      submittedKeyRef.current = null
      setLoginCode(null)
      setSignupSession(null)
      setVisibleError(META_EMBEDDED_SIGNUP_TIMEOUT_NOTIFICATION)
    }, META_EMBEDDED_SIGNUP_TIMEOUT_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [flowStatus, loginCode, setVisibleError, signupSession])

  const launchWhatsAppSignup = useCallback(() => {
    if (disabled || flowStatus === 'opening' || flowStatus === 'submitting') {
      return
    }

    if (configError) {
      setVisibleError(META_EMBEDDED_SIGNUP_UNAVAILABLE_NOTIFICATION)
      return
    }

    if (sdkStatus !== 'ready' || !window.FB) {
      const sdkMessage = sdkStatus === 'error'
        ? META_EMBEDDED_SIGNUP_UNAVAILABLE_NOTIFICATION
        : 'Meta WhatsApp connection is still loading. Try again in a moment.'
      setVisibleError(sdkMessage)
      if (sdkStatus === 'error') {
        void prepareSdk()
      }
      return
    }

    activeFlowRef.current = true
    submittedKeyRef.current = null
    setLoginCode(null)
    setSignupSession(null)
    setFlowStatus('opening')

    window.FB.login(
      (response) => {
        console.debug('[MetaEmbeddedSignup] Login response:', response)

        if (!activeFlowRef.current) {
          return
        }

        const code = response.authResponse?.code
        if (code) {
          setLoginCode(code)
          setFlowStatus((current) => (current === 'submitting' ? current : 'waiting'))
          return
        }

        activeFlowRef.current = false
        submittedKeyRef.current = null
        setVisibleError(META_EMBEDDED_SIGNUP_ERROR_NOTIFICATION)
      },
      {
        config_id: configurationId,
        response_type: 'code',
        override_default_response_type: true,
        extras: META_EMBEDDED_SIGNUP_LOGIN_EXTRAS,
      }
    )
  }, [
    configError,
    configurationId,
    disabled,
    flowStatus,
    prepareSdk,
    sdkStatus,
    setVisibleError,
  ])

  const busy = (
    sdkStatus === 'loading' ||
    flowStatus === 'opening' ||
    flowStatus === 'waiting' ||
    flowStatus === 'submitting'
  )

  return {
    busy,
    buttonLabel: getButtonLabel(flowStatus, sdkStatus),
    configError,
    flowStatus,
    launchWhatsAppSignup,
    sdkStatus,
  }
}
